import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { environment } from '@env';

/**
 * CryptoService
 *
 * Provides AES-256 (CBC mode via crypto-js) encryption and decryption
 * for sensitive values stored in cookies or local state.
 *
 * Note: The secret lives in the compiled bundle, so this protects against
 * casual inspection and XSS cookie-reading, not against a determined
 * attacker who decompiles the bundle. Server-side token validation is
 * the primary security layer.
 */
@Injectable({ providedIn: 'root' })
export class CryptoService {
  private readonly secret = environment.encryptionSecret;

  // ── Primitives ─────────────────────────────────────────

  /** Encrypt a plain string → AES-256 ciphertext (base64) */
  encrypt(plainText: string): string {
    return CryptoJS.AES.encrypt(plainText, this.secret).toString();
  }

  /** Decrypt an AES-256 ciphertext (base64) → plain string */
  decrypt(cipherText: string): string {
    const bytes = CryptoJS.AES.decrypt(cipherText, this.secret);
    return bytes.toString(CryptoJS.enc.Utf8);
  }

  // ── Object helpers ──────────────────────────────────────

  /** Serialize an object to JSON then encrypt */
  encryptObject<T>(obj: T): string {
    return this.encrypt(JSON.stringify(obj));
  }

  /** Decrypt then parse JSON back to the original type. Returns null on failure. */
  decryptObject<T>(cipherText: string): T | null {
    try {
      const plain = this.decrypt(cipherText);
      return plain ? (JSON.parse(plain) as T) : null;
    } catch {
      return null;
    }
  }

  // ── HMAC ───────────────────────────────────────────────

  /** Generate an HMAC-SHA256 signature for integrity checks */
  sign(value: string): string {
    return CryptoJS.HmacSHA256(value, this.secret).toString(CryptoJS.enc.Hex);
  }

  /** Verify that a value matches its HMAC-SHA256 signature */
  verify(value: string, signature: string): boolean {
    return this.sign(value) === signature;
  }
}
